import { BannerItem } from "@/utils/library/banner";

export const BANNER_CACHE_KEY = "GAIL_banner_cache";
export const BANNER_CACHE_TTL_MS = 10 * 60 * 1000;

const BOT_USER_AGENT =
  /bot|crawl|spider|slurp|headless|lighthouse|facebookexternalhit|embedly|preview/i;

export type BannerNavigator = {
  userAgent: string;
  webdriver?: boolean;
};

type CachedBanners = {
  fetchedAt: number;
  items: BannerItem[];
};

export function isLikelyBot(nav: BannerNavigator): boolean {
  if (nav.webdriver) return true;
  return BOT_USER_AGENT.test(nav.userAgent);
}

export function readCachedBanners(
  storage: Storage,
  now = Date.now(),
): BannerItem[] | null {
  try {
    const raw = storage.getItem(BANNER_CACHE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isCachedBanners(parsed)) return null;
    if (now - parsed.fetchedAt > BANNER_CACHE_TTL_MS) return null;

    return parsed.items;
  } catch {
    return null;
  }
}

export function writeCachedBanners(
  storage: Storage,
  items: BannerItem[],
  now = Date.now(),
): void {
  try {
    const payload: CachedBanners = { fetchedAt: now, items };
    storage.setItem(BANNER_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage can throw when it is disabled or full. A missed write
    // only costs another request later.
  }
}

export function filterAckedBannerItems(
  items: BannerItem[],
  ackedIdsString: string | null,
): BannerItem[] {
  const ackedIds = ackedIdsString ? ackedIdsString.split(",") : null;

  return items.filter((item) => {
    if (ackedIds?.includes(item.id) && !item.isPersistent) {
      return false;
    }
    return true;
  });
}

export async function loadBannerItems(
  nav?: BannerNavigator,
  storage?: Storage,
  now = Date.now(),
): Promise<BannerItem[]> {
  try {
    const resolvedNav = nav ?? currentNavigator();
    if (isLikelyBot(resolvedNav)) return [];

    // Reading sessionStorage can throw when the browser blocks storage.
    // That has to happen inside this catch, not as a default argument.
    const resolvedStorage = storage ?? sessionStorage;
    const cached = readCachedBanners(resolvedStorage, now);
    if (cached) return cached;

    const response = await fetch("/api/banner");
    if (!response.ok) return [];

    const items: unknown = await response.json();
    if (!Array.isArray(items)) return [];

    writeCachedBanners(resolvedStorage, items, now);
    return items;
  } catch {
    return [];
  }
}

function currentNavigator(): BannerNavigator {
  return {
    userAgent: navigator.userAgent,
    webdriver: navigator.webdriver,
  };
}

function isCachedBanners(value: unknown): value is CachedBanners {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<CachedBanners>;
  return (
    typeof candidate.fetchedAt === "number" && Array.isArray(candidate.items)
  );
}
